// UsersPage.tsx · v12.10.26 (#2133 ⋮ › Copy invite link) · 2026-10-30 · VoiceSpectrum AI
// UsersPage.tsx · v12.10.25 (#2106 ⋮ › Show welcome again) · 2026-10-30 · VoiceSpectrum AI
// UsersPage.tsx · v12.10.23 (#2071 who is signed in now - a green dot on the avatar, and under the name Active / Idle + how long the session
//   has been open; idle = 10 min with no click / API call; two browsers of one person count as one, the newest) · 2026-10-29 · VoiceSpectrum AI
// UsersPage.tsx · v12.10.9.3 (#1327 a person with no name shows the email once) · 2026-10-07
// VoiceSpectrum.Web.T2 · v12.10.9 (Security Phase 1 – #1258/#1259/#1260/#1261) · 2026-10-06
// UsersPage.tsx - Settings › Security › Users (#1259; prototype scUsersPage / scPaintUsers / scUserMenu).
// Everyone who can sign in (support staff never appear - S34): search, All · Active · Disabled (invited and locked count as
// Active - §30), group filter, the "hasn't set up a second step" banner, one row per person with group(s), 2-step state and
// last sign-in, a ⋮ menu (§22), the person drawer, and Invite user (the page's one primary action, in the page head).
// A person's own row opens Your account.
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Avatar, SecondStepChip, StatusTags, displayName, enforceShort, groupById, groupName, groupsFull, groupsLabel, identity, isInvite, isLocked, requiredFor, sortGroups,
} from '../../components/security/people';
import { openYourAccount } from '../../components/security/YourAccountDrawer';
import { Ic, digits, fmtDate, fmtWhen, plural, useConfirm, useMenu, type MenuItem } from '../../components/security/ui';
import { securityApi, securityErrorMessage, type Group, type UserRow } from '../../lib/securityApi';
import { SECURITY_CHANGED_EVENT, useAccess } from '../../lib/useAccess';
import InviteModal from './InviteModal';
import { InviteLinkHost } from '../../components/security/InviteLinkModal';   // v12.10.26 (#2133)
import { GROUPS_KEY, USERS_KEY } from './PlatformPage';
import UserDrawer from './UserDrawer';
import { makeUserActions } from './userActions';

type StatusFilter = 'all' | 'active' | 'disabled';
// v12.10.23 (#2071): "1 h 20 m" / "12 m" / "just now"
const IDLE_MS = 10 * 60_000;
function span(fromIso: string | null | undefined, now: number): string {
  const t = fromIso ? new Date(fromIso).getTime() : NaN;
  if (Number.isNaN(t)) return '';
  const m = Math.max(0, Math.floor((now - t) / 60_000));
  if (m < 1) return 'just now';
  return m >= 60 ? `${Math.floor(m / 60)} h ${m % 60} m` : `${m} m`;
}
function presenceOf(u: UserRow, now: number): 'active' | 'idle' | null {
  if (!u.signedInAt || u.status === 'disabled') return null;
  const seen = u.lastSeenAt ? new Date(u.lastSeenAt).getTime() : NaN;
  // the server's answer, turned to Idle on this page once 10 minutes pass without a call
  return u.presence === 'idle' || (!Number.isNaN(seen) && now - seen >= IDLE_MS) ? 'idle' : 'active';
}
const STATUS_ORDER: Record<string, number> = { active: 0, invited: 1, expired: 1, disabled: 2 };

export default function UsersPage({ inviteOpen, onInviteClose, groupFilter, onGroupFilter }: {
  inviteOpen: boolean; onInviteClose: () => void; groupFilter: number | 'all'; onGroupFilter: (g: number | 'all') => void;
}) {
  const qc = useQueryClient();
  const { me, hasAccess } = useAccess();
  const policy = me?.policy ?? null;
  const usersQ = useQuery<UserRow[]>({ queryKey: USERS_KEY, queryFn: () => securityApi.users(), retry: false });
  // The groups list is needed for names and the picker; it may be refused for a person without User groups access.
  const groupsQ = useQuery<{ groups: Group[] }>({ queryKey: GROUPS_KEY, queryFn: () => securityApi.groups(), retry: false });

  const [q, setQ] = useState('');
  const [status, setStatus] = useState<StatusFilter>('active');
  const [onlyMissing, setOnlyMissing] = useState(false);
  const [openId, setOpenId] = useState<number | null>(null);
  const [now, setNow] = useState(() => Date.now());
  const [confirmEl, ask] = useConfirm();
  const menu = useMenu('Person actions');

  useEffect(() => { const t = window.setInterval(() => setNow(Date.now()), 20000); return () => window.clearInterval(t); }, []);
  useEffect(() => {
    const on = () => { qc.invalidateQueries({ queryKey: USERS_KEY }); };
    window.addEventListener(SECURITY_CHANGED_EVENT, on);
    return () => window.removeEventListener(SECURITY_CHANGED_EVENT, on);
  }, [qc]);
  useEffect(() => { if (groupFilter !== 'all') setStatus('all'); }, [groupFilter]);

  const users = useMemo(() => usersQ.data ?? [], [usersQ.data]);
  const groups = useMemo(() => groupsQ.data?.groups ?? [], [groupsQ.data]);

  const onChanged = useCallback((id: number, row: UserRow | null, removed?: boolean) => {
    qc.setQueryData<UserRow[]>(USERS_KEY, (old) => {
      if (!old) return old;
      if (removed) return old.filter((u) => u.id !== id);
      if (row) return old.map((u) => (u.id === id ? row : u));
      return old;
    });
    qc.invalidateQueries({ queryKey: USERS_KEY });
    qc.invalidateQueries({ queryKey: GROUPS_KEY });
  }, [qc]);
  const actions = useMemo(() => makeUserActions({ users, groups, policy, ask, onChanged }), [users, groups, policy, ask, onChanged]);

  const active = users.filter((u) => u.status === 'active');
  const missing = active.filter((u) => requiredFor(u, policy, groups) && !(u.methods ?? []).length);
  const counts = {
    all: users.length,
    active: users.filter((u) => u.status !== 'disabled').length,
    disabled: users.filter((u) => u.status === 'disabled').length,
  };

  const list = useMemo(() => {
    const ql = q.trim().toLowerCase();
    const qd = digits(ql);
    return users.filter((u) => {
      if (status === 'active' && u.status === 'disabled') return false;
      if (status === 'disabled' && u.status !== 'disabled') return false;
      if (onlyMissing && !(u.status === 'active' && requiredFor(u, policy, groups) && !(u.methods ?? []).length)) return false;
      if (groupFilter !== 'all' && !u.groups.includes(groupFilter)) return false;
      if (ql && !(displayName(u).toLowerCase().includes(ql) || (u.email ?? '').toLowerCase().includes(ql) || (qd.length >= 3 && digits(u.mobile).includes(qd)))) return false;
      return true;
    }).sort((a, b) => (STATUS_ORDER[a.status] - STATUS_ORDER[b.status]) || displayName(a).localeCompare(displayName(b)));
  }, [users, q, status, onlyMissing, groupFilter, policy, groups]);

  function open(u: UserRow) {
    if (u.self) { openYourAccount(); return; }
    setOpenId(u.id);
  }

  function menuFor(btn: HTMLElement, u: UserRow) {
    const items: MenuItem[] = [];
    const can = (a: string) => hasAccess('sec_users', a);
    if (isLocked(u, now) && can('unlock')) items.push({ icon: 'unlock', label: 'Unlock now', onClick: () => void actions.unlock(u) });
    items.push({ icon: 'user', label: u.self ? 'Open your account' : 'Open profile', onClick: () => open(u) });
    if (isInvite(u)) {
      if (can('invite')) {
        items.push({ icon: 'send', label: 'Resend invite', onClick: () => void actions.resend(u) });
        items.push({ icon: 'copy', label: 'Copy invite link', onClick: () => void actions.copyInviteLink(u) });   // v12.10.26 (#2133)
        items.push('sep');
        items.push({ icon: 'x', label: 'Cancel invite', danger: true, onClick: () => actions.cancelInvite(u) });
      }
    } else if (u.status === 'disabled') {
      if (can('disable')) items.push({ icon: 'unlock', label: 'Enable user', onClick: () => actions.enable(u) });
      if (can('del')) { items.push('sep'); items.push({ icon: 'trash', label: 'Delete user', danger: true, onClick: () => actions.del(u) }); }
    } else {
      if (can('pwreset')) items.push({ icon: 'key', label: 'Send password reset link', onClick: () => actions.pwReset(u) });
      if (can('mfa') && (u.methods ?? []).length && !u.self) items.push({ icon: 'reset', label: 'Reset 2-step verification', onClick: () => actions.reset2(u) });
      if (can('signout') && !u.self) items.push({ icon: 'out', label: 'Sign out everywhere', onClick: () => void actions.signOut(u) });
      if (can('edit')) items.push({ icon: 'reset', label: 'Show welcome again', onClick: () => actions.showWelcomeAgain(u) });   // v12.10.25 (#2106)
      if (can('disable') && !u.self) { items.push('sep'); items.push({ icon: 'ban', label: 'Disable user', danger: true, onClick: () => actions.disable(u) }); }
    }
    if (items[items.length - 1] === 'sep') items.pop();
    menu.open(btn, items);
  }

  const groupCell = (u: UserRow) => {
    const first = groupById(groups, u.groups[0]);
    const many = u.groups.length > 1;
    const seesMessages = u.groups.some((id) => { const g = groupById(groups, id); return !!g && ((g.grants.phone ?? []).includes('view') || (g.grants.texts ?? []).includes('view')); });
    return (
      <div className="sc-acc" title={groupsFull(u.groups, groups)}>
        <span className="sc-acc-name">{groups.length ? groupsLabel(u.groups, groups) : plural(u.groups.length, 'group')}{first && !first.builtin && !many && <> <span className="sc-rtag">Custom</span></>}</span>
        {groups.length > 0 && <span className="sc-acc-flags"><span>{many ? groupsFull(u.groups, groups) : seesMessages ? 'Sees everyone’s messages' : 'No access to messages'}</span></span>}
      </div>
    );
  };

  if (usersQ.isLoading) return <div className="vs-sec sec-page"><div className="sec-loading">Loading people…</div></div>;
  if (usersQ.isError) return <div className="settings-error">{securityErrorMessage(usersQ.error, 'The list of people could not be loaded.')}</div>;

  const openUser = openId !== null ? users.find((u) => u.id === openId) ?? null : null;

  return (
    <div className="vs-sec sec-page">
      {missing.length > 0 && policy && policy.mfaRequired !== 'off' && (
        <div className="sc-warn" style={{ maxWidth: 1100, marginBottom: 14 }}><Ic k="warn" /><div>
          <b>{plural(missing.length, 'person hasn’t', 'people haven’t')} set up a second step.</b> They’ll be asked at next sign-in, and must have one from {enforceShort(policy)}.{' '}
          <button type="button" className="sc-link" onClick={() => setOnlyMissing(!onlyMissing)}>{onlyMissing ? 'Show everyone' : 'Show only them'}</button>
        </div></div>
      )}
      {/* toolbar: wraps at narrow widths (search takes its own row) - §11 rule four */}
      <div className="sc-toolbar">
        <label className="sc-search"><Ic k="search" /><input placeholder="Search by name, email or phone" value={q} onChange={(e) => setQ(e.target.value)} aria-label="Search users" /></label>
        <div className="seg-group" role="radiogroup" aria-label="Status">
          {([['all', 'All'], ['active', 'Active'], ['disabled', 'Disabled']] as const).map(([k, l]) => (
            <button key={k} type="button" className={`seg${status === k ? ' on' : ''}`} role="radio" aria-checked={status === k} onClick={() => setStatus(k)}><span className="seg-dot" />{l} {counts[k]}</button>
          ))}
        </div>
        {groups.length > 0 && (
          <select aria-label="Group" value={groupFilter === 'all' ? 'all' : String(groupFilter)} onChange={(e) => onGroupFilter(e.target.value === 'all' ? 'all' : Number(e.target.value))}>
            <option value="all">All groups</option>
            {sortGroups(groups).map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
          </select>
        )}
      </div>
      {groupFilter !== 'all' && (
        <div style={{ margin: '-4px 0 10px' }}><span className="sc-filter-chip">Group: {groupName(groups, groupFilter)}<button type="button" aria-label="Clear group filter" onClick={() => onGroupFilter('all')}><Ic k="x" /></button></span></div>
      )}
      <div className="sc-count">Showing {plural(list.length, 'person', 'people')}</div>
      <div className="sc-users">
        {!list.length ? (
          <div className="sc-empty"><b>No one matches</b><span>Try a different name, or clear the filters.</span>
            <button type="button" className="sc-secondary" onClick={() => { setQ(''); setStatus('all'); onGroupFilter('all'); setOnlyMissing(false); }}>Clear filters</button></div>
        ) : list.map((u) => {
          const inv = isInvite(u), dis = u.status === 'disabled';
          const name = displayName(u);
          const sub = inv
            ? `Invited ${fmtDate(u.invitedAt) || ''} by ${u.email ? 'email' : 'text'} · ${u.status === 'expired' ? `expired ${fmtDate(u.inviteExpiresAt, { month: 'short', day: 'numeric' })}` : `expires ${fmtDate(u.inviteExpiresAt, { month: 'short', day: 'numeric' })}`}`
            : null;
          return (
            <div key={u.id} className={`sc-user ${u.status}`} role="button" tabIndex={0} aria-label={`Open ${name}`}
              onClick={() => open(u)} onKeyDown={(e) => { if (e.key === 'Enter' && e.target === e.currentTarget) open(u); }}>
              {/* v12.10.23 (#2071): a green dot on the avatar while the person is signed in (grey while idle) */}
              <span className="sc-av-wrap"><Avatar u={u} />{presenceOf(u, now) && <i className={`sc-on-dot ${presenceOf(u, now)}`} title={presenceOf(u, now) === 'active' ? 'Signed in - active' : 'Signed in - idle'} aria-hidden="true" />}</span>
              <div className="sc-u-main">
                <div className="sc-u-name"><span className="nm">{inv && !(u.firstName || u.lastName) ? identity(u) : name}</span>{u.self && <span className="sc-you">You</span>}<StatusTags u={u} /></div>
                {/* v12.10.9.3 (#1327): no name -> the email is the name, shown once (as #1300 on the consoles) */}
                {(sub || !(u.email && !(u.firstName || u.lastName) && name === u.email)) && <div className="sc-u-mail">{sub ?? (u.email ? u.email : <><svg className="sc-ph-ic" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} aria-hidden="true"><rect x="6" y="2" width="12" height="20" rx="2" /><path d="M11 18h2" /></svg>{u.mobile}</>)}</div>}
                {(u.invitedBy && inv) && <div className="sc-u-mail">by {u.invitedBy}{u.requestedBy ? ` · requested by ${u.requestedBy}` : ''}</div>}
                {/* v12.10.23 (#2071): Active / Idle (since the last click or API call) · how long the session has been open */}
                {presenceOf(u, now) && (
                  <div className={`sc-u-on ${presenceOf(u, now)}`}>
                    <b>{presenceOf(u, now) === 'active' ? 'Active' : `Idle ${span(u.lastSeenAt, now)}`}</b> · Signed in {span(u.signedInAt, now)}
                  </div>
                )}
              </div>
              {groupCell(u)}
              <div className="sc-2fa-cell"><SecondStepChip u={u} policy={policy} groups={groups} now={now} /></div>
              <div className="sc-last"><b>{inv ? 'Invite' : 'Last sign-in'}</b>{inv ? 'Hasn’t signed in' : (fmtWhen(u.lastLoginAt) || 'Never')}</div>
              <button type="button" className="sc-kebab" aria-label={`Actions for ${name}`} aria-haspopup="menu" aria-expanded={menu.openFor !== null && menu.openFor.getAttribute('aria-label') === `Actions for ${name}`}
                onClick={(e) => { e.stopPropagation(); menuFor(e.currentTarget, u); }} onKeyDown={(e) => e.stopPropagation()}><Ic k="kebab" /></button>
            </div>
          );
        })}
      </div>
      {menu.el}
      {openUser && !openUser.self && (
        <UserDrawer key={openUser.id} user={openUser} users={users} groups={groups} policy={policy} actions={actions}
          onClose={() => setOpenId(null)} onSaved={(row) => onChanged(openUser.id, row)} />
      )}
      <InviteLinkHost />{/* v12.10.26 (#2133): the shown-once invite link dialog */}
      {inviteOpen && <InviteModal groups={groups} users={users} onClose={onInviteClose} onInvited={() => { setStatus('active'); qc.invalidateQueries({ queryKey: USERS_KEY }); qc.invalidateQueries({ queryKey: GROUPS_KEY }); }} />}
      {confirmEl}
    </div>
  );
}
